import React, { useState, useEffect } from "react";
import UoMForm from "../../components/unit-of-measure/UoMForm";
import UoMTable from "../../components/unit-of-measure/UoMTable";
import { useItemsFilter } from "../../../_helper/actions/app/items";
import { ApiEndPoint } from "../../../_helper/constant/api-types";
import { useAtom } from "jotai";
import {
  clinicAtom,
  pharmacyAtom,
} from "../../../_helper/constant/constant-atom";
import {
  useCreateItem,
  useDeleteItem,
  useUpdateItem,
} from "../../../_helper/actions/mutation/items";
import { toast } from "react-toastify";
import LinkedDoctorForm from "../../components/linked-doctor-pharmacy/LinkedDoctorForm";
import LinkedDoctorsTable from "../../components/linked-doctor-pharmacy/LinkedDoctorsTable";

export default function LinkedDoctorPharmacy() {
  // Use both value and setter for pharmacyAtom
  const [pharmacy, setPharmacy] = useAtom(pharmacyAtom);
  const [clinic] = useAtom(clinicAtom);
  const [uomData, setUomData] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // This state is used only when a clinic exists
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);

  // When clinic exists, build a query to fetch the pharmacies related to that clinic.
  const clinicQuery = clinic?.id
    ? `?action=getPharmacy&clinic_id=${clinic?.id}`
    : null;
  const { data: pharmacies, isFetched } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    clinicQuery,
    !!clinic?.id
  );

  // When a clinic is present and pharmacies are fetched, default to the first pharmacy.
  useEffect(() => {
    if (clinic?.id && isFetched && pharmacies?.length > 0) {
      setSelectedPharmacy(pharmacies[0]?.id);
    }
  }, [clinic?.id, isFetched, pharmacies]);
  // Fetch data based on pharmacy and clinic
  const { data, isLoading, error } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    `?action=getPharmacyLinkedDoctor&pharmacy_id=${selectedPharmacy ? selectedPharmacy : pharmacy?.id}&clinic_id=${clinic?.id}`
  );

  // When new API data is available, update the local state
  useEffect(() => {
    if (data) {
      setUomData(data);
    }
  }, [data]);

  const createMutation = useCreateItem(ApiEndPoint.PHARMACY);
  const updateMutation = useUpdateItem(ApiEndPoint.PHARMACY);
  const deleteMutation = useDeleteItem(ApiEndPoint.PHARMACY);

  // Save (Create or Update) a Unit of Measure
  const handleSaveUoM = (newData) => {
    if (newData.id) {
      // Update existing item
      updateMutation.mutate(
        {
          id: newData.id,
          data: {
            action: "patchPharmacyLinkedDoctor",
            ...newData,
          },
        },
        {
          onSuccess: () => {
            toast.success("Edited the doctor!");
            setIsFormOpen(false);
          },
          onError: () => {
            toast.error("Error updating doctor!");
          },
        }
      );
    } else {
      // Create a new item
      createMutation.mutate(
        {
          action: "postPharmacyLinkedDoctor",
          pharmacy_id: selectedPharmacy ? selectedPharmacy : pharmacy?.id,
          clinic_id: clinic?.id,
          ...newData,
        },
        {
          onSuccess: () => {
            toast.success("Added the Doctor!");
            setIsFormOpen(false);
          },
          onError: () => {
            toast.error("Error adding new doctor");
          },
        }
      );
    }
    setEditData(null);
  };

  // Delete a Unit of Measure locally (ideally, you'd also call a delete API)
  const handleDeleteUoM = (id) => {
    deleteMutation.mutate(
      { data: { action: "delPharmacyLinkedDoctor", id: id } },
      {
        onSuccess: () => {
          toast.success("Doctor deleted successfully!");
        },
      }
    );
    // setUomData((prev) => prev.filter((item) => item.id !== id));
    // toast.success("Deleted the Conversion Factor");
  };

  // Prepare the form for editing an existing Unit of Measure
  const handleEditUoM = (item) => {
    setEditData(item);
    setIsFormOpen(true);
  };

  return (
    <div className="p-4">
      {/* Pharmacy Dropdown aligned to the right */}
      <div className="flex justify-between mb-4">
        <h1 className="text-2xl font-bold ">Linked Doctors</h1>
        {/* Pharmacy Selector or Display */}
        <div className="relative w-50">
          {clinic?.id ? (
            // When a clinic exists, show a dropdown selector.
            <select
              value={selectedPharmacy}
              onChange={(e) => setSelectedPharmacy(e.target.value)}
              className="block w-full bg-white text-lg font-semibold text-gray-800 px-4 py-3 appearance-none cursor-pointer transition-all duration-200 hover:shadow-lg"
            >
              {pharmacies?.map((pharmacy, index) => (
                <option
                  key={index}
                  value={pharmacy.id}
                  className="text-base font-medium text-gray-700"
                >
                  {pharmacy.pharmacy_name}
                </option>
              ))}
            </select>
          ) : (
            // When no clinic exists, display the pharmacy name from pharmacyAtom.
            <div className="block w-full bg-white text-lg font-semibold text-gray-800 px-4 py-3">
              {pharmacy?.pharmacy_name}
            </div>
          )}

          {/* Custom Dropdown Icon (only shown if the dropdown is rendered) */}
          {clinic?.id && (
            <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none -m-4">
              <svg
                className="w-6 h-6 text-gray-500 transition-transform duration-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </div>
          )}
        </div>
      </div>

      {isFormOpen ? (
        <LinkedDoctorForm
          onSave={handleSaveUoM}
          onCancel={() => {
            setIsFormOpen(false);
            setEditData(null);
          }}
          editData={editData}
        />
      ) : (
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded mb-4"
          onClick={() => setIsFormOpen(true)}
        >
          Add Doctor
        </button>
      )}
      <LinkedDoctorsTable
        data={uomData}
        onEdit={handleEditUoM}
        onDelete={handleDeleteUoM}
        isLoading={isLoading}
      />
      {/* <UnitConverter data={uomData} /> */}
    </div>
  );
}
